import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import {
  PostAppointmentAttachment,
  PostAppointmentAttachmentPayload,
  PostAppointmentAttachmentSourceType,
  PostAppointmentOffsetUnit,
  PendingPostAppointmentAttachment,
} from "../../types/servicePostAppointment";
import AssignPostAppointmentAttachmentModal, {
  makePendingAttachment,
} from "./AssignPostAppointmentAttachmentModal";
import AttachmentBadge from "./AttachmentBadge";

export type PostAppointmentMessageDraft = {
  offset_value: number;
  offset_unit: PostAppointmentOffsetUnit;
  label: string;
  attachments: PendingPostAppointmentAttachment[];
};

interface PostAppointmentMessageModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (
    draft: PostAppointmentMessageDraft,
    payload: { attachments: PostAppointmentAttachmentPayload[] },
  ) => Promise<void> | void;
  initial?: Partial<PostAppointmentMessageDraft>;
  isEditing?: boolean;
  isSaving?: boolean;
}

const UNIT_OPTIONS: { value: PostAppointmentOffsetUnit; label: string }[] = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

const EMPTY_DRAFT: PostAppointmentMessageDraft = {
  offset_value: 1,
  offset_unit: "days",
  label: "",
  attachments: [],
};

/** Convert saved attachments to the pending shape the modal edits. */
export function seedAttachmentsFromMessage(
  list: PostAppointmentAttachment[],
  resolveName: (a: PostAppointmentAttachment) => string,
): PendingPostAppointmentAttachment[] {
  return list
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((a) => ({
      tempId: a.uuid,
      source_type: a.source_type,
      source_id: a.source_id,
      name: resolveName(a),
      sort_order: a.sort_order,
    }));
}

export const PostAppointmentMessageModal: React.FC<
  PostAppointmentMessageModalProps
> = ({
  opened,
  onClose,
  onSave,
  initial,
  isEditing = false,
  isSaving = false,
}) => {
  const [draft, setDraft] = useState<PostAppointmentMessageDraft>(EMPTY_DRAFT);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emptyWarningOpen, setEmptyWarningOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDraft({
        offset_value: initial?.offset_value ?? EMPTY_DRAFT.offset_value,
        offset_unit: initial?.offset_unit ?? EMPTY_DRAFT.offset_unit,
        label: initial?.label ?? "",
        attachments: initial?.attachments ?? [],
      });
      setValidationError(null);
      setEmptyWarningOpen(false);
    }
  }, [opened, initial]);

  const existingKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of draft.attachments) {
      set.add(`${a.source_type}:${a.source_id}`);
    }
    return set;
  }, [draft.attachments]);

  const handleAddAttachments = (
    picks: Array<{
      source_type: PostAppointmentAttachmentSourceType;
      source_id: string;
      name: string;
    }>,
  ) => {
    setDraft((prev) => {
      const next = prev.attachments.slice();
      let nextSort = next.length;
      for (const p of picks) {
        next.push(makePendingAttachment(p, nextSort++));
      }
      return { ...prev, attachments: next };
    });
    setPickerOpen(false);
  };

  const handleRemove = (tempId: string) => {
    setDraft((prev) => ({
      ...prev,
      attachments: prev.attachments
        .filter((a) => a.tempId !== tempId)
        .map((a, i) => ({ ...a, sort_order: i })),
    }));
  };

  const move = (tempId: string, direction: -1 | 1) => {
    setDraft((prev) => {
      const idx = prev.attachments.findIndex((a) => a.tempId === tempId);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.attachments.length) return prev;
      const copy = prev.attachments.slice();
      const [moved] = copy.splice(idx, 1);
      copy.splice(target, 0, moved);
      return {
        ...prev,
        attachments: copy.map((a, i) => ({ ...a, sort_order: i })),
      };
    });
  };

  const persist = async () => {
    const payload: PostAppointmentAttachmentPayload[] = draft.attachments.map(
      (a, i) => ({
        source_type: a.source_type,
        source_id: a.source_id,
        sort_order: i,
      }),
    );
    await onSave(draft, { attachments: payload });
  };

  const handleSaveClick = () => {
    if (
      typeof draft.offset_value !== "number" ||
      draft.offset_value < 0 ||
      draft.offset_value > 365
    ) {
      setValidationError("Offset must be between 0 and 365.");
      return;
    }
    setValidationError(null);
    if (draft.attachments.length === 0) {
      setEmptyWarningOpen(true);
      return;
    }
    void persist();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Text fw={700}>
            {isEditing
              ? "Edit post-appointment message"
              : "Add post-appointment message"}
          </Text>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          {validationError && (
            <Alert color="red" icon={<IconAlertCircle size={14} />}>
              {validationError}
            </Alert>
          )}

          <Group align="flex-end" gap="sm" wrap="nowrap">
            <NumberInput
              label="Send after"
              value={draft.offset_value}
              min={0}
              max={365}
              onChange={(v) =>
                setDraft((prev) => ({
                  ...prev,
                  offset_value: typeof v === "number" ? v : Number(v) || 0,
                }))
              }
              style={{ flex: 1 }}
              aria-label="Offset value"
            />
            <Select
              label="Unit"
              data={UNIT_OPTIONS}
              value={draft.offset_unit}
              onChange={(v) =>
                v &&
                setDraft((prev) => ({
                  ...prev,
                  offset_unit: v as PostAppointmentOffsetUnit,
                }))
              }
              style={{ width: 140 }}
              allowDeselect={false}
            />
          </Group>

          <TextInput
            label="Label (optional)"
            placeholder="e.g. 2-week check-in"
            value={draft.label}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, label: e.currentTarget.value }))
            }
          />

          <Divider label="Attachments" labelPosition="left" />

          {draft.attachments.length === 0 ? (
            <Alert
              color="gray"
              variant="light"
              icon={<IconAlertCircle size={14} />}
            >
              No attachments yet. Add at least one document or form template so
              the post-appointment message has content to deliver.
            </Alert>
          ) : (
            <Stack gap="xs">
              {draft.attachments.map((a, idx) => (
                <Card key={a.tempId} withBorder padding="xs" radius="sm">
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {a.name}
                      </Text>
                      <AttachmentBadge sourceType={a.source_type} />
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Move up">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          disabled={idx === 0}
                          onClick={() => move(a.tempId, -1)}
                          aria-label="Move up"
                        >
                          <IconArrowUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Move down">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          disabled={idx === draft.attachments.length - 1}
                          onClick={() => move(a.tempId, 1)}
                          aria-label="Move down"
                        >
                          <IconArrowDown size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Remove">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleRemove(a.tempId)}
                          aria-label="Remove attachment"
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}

          <Box>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={14} />}
              onClick={() => setPickerOpen(true)}
            >
              Add attachment
            </Button>
          </Box>

          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={handleSaveClick}
              loading={isSaving}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <AssignPostAppointmentAttachmentModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAssign={handleAddAttachments}
        existingKeys={existingKeys}
      />

      <Modal
        opened={emptyWarningOpen}
        onClose={() => setEmptyWarningOpen(false)}
        title={<Text fw={700}>No attachments selected</Text>}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            You didn&apos;t attach anything for this message — continue?
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setEmptyWarningOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={async () => {
                setEmptyWarningOpen(false);
                await persist();
              }}
              loading={isSaving}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default PostAppointmentMessageModal;
